
import type { ReactNode } from 'react';
import { Badge, Button, Card, Heading, Row, Stack, Text, Time, TimeRange } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { formatCalendarDate } from '@client/shared/lib/wall-clock-intake.ts';
import { useListingCapReached } from '@client/features/group';
import type { ListingCard } from './listings-operations.ts';
import styles from './Listings.module.css';

export function listingHref(listingId: string): string {
  return `/listings/${listingId}`;
}

export const NEW_LISTING_HREF = '/listings/new';

export function AddListingLink({
  variant = 'primary',
  children,
}: {
  variant?: 'primary' | 'secondary';
  children: ReactNode;
}) {
  const capReached = useListingCapReached();
  if (capReached) {
    const reason = 'All listing slots are in use. Archive a listing to make room, or upgrade in group settings.';
    return (
      <span
        className={cx(styles.add, variant === 'secondary' && styles.addSoft, styles.addDisabled)}
        aria-disabled="true"
        title={reason}
      >
        <PlusGlyph />
        {children}
      </span>
    );
  }
  return (
    <a href={NEW_LISTING_HREF} className={cx(styles.add, variant === 'secondary' && styles.addSoft)}>
      <PlusGlyph />
      {children}
    </a>
  );
}

export function ListingsGrid({ rows }: { rows: readonly ListingCard[] }) {
  return (
    <ul className={styles.grid}>
      {rows.map((row, i) => (
        <li key={row.id} className="reveal" style={{ ['--i' as string]: Math.min(i, 6) }}>
          <ListingRow row={row} />
        </li>
      ))}
    </ul>
  );
}

function ListingRow({ row }: { row: ListingCard }) {
  const labelSuffix = row.archived ? ' (archived)' : row.canBook ? '' : ' (view only)';
  return (
    <a
      href={listingHref(row.id)}
      className={styles.cardLink}
      aria-label={`Open the details for ${row.address}${labelSuffix}`}
    >
      <Card pad="md" interactive spine className={cx(styles.card, row.archived && styles.cardArchived, !row.nextBooking && !row.archived && styles.cardSpineGray)}>
        <Stack gap={2} className={styles.cardBody}>
          <ListingEyebrow row={row} />
          <Row gap={2} align="center" className={styles.addressLine}>
            <Text weight="semibold" className={styles.address}>
              {row.address}
            </Text>
            {row.archived ? (
              <Badge tone="warn" dot>
                Archived
              </Badge>
            ) : (
              !row.canBook && <Badge tone="neutral">View only</Badge>
            )}
          </Row>
          {row.locality && (
            <Text size="sm" tone="soft" className={styles.locality}>
              {row.locality}
            </Text>
          )}
        </Stack>
      </Card>
    </a>
  );
}

function ListingEyebrow({ row }: { row: ListingCard }) {
  if (row.archived) {
    return (
      <span className={styles.eyebrow}>
        <span className={cx(styles.eyebrowMeta, styles.eyebrowArchived)}>Archived</span>
      </span>
    );
  }
  if (row.nextBooking) {
    return (
      <span className={cx(styles.eyebrow, styles.eyebrowActive)}>
        <span className={styles.eyebrowLabel}>
          {row.isViewerNextBooking ? 'Your next open house' : 'Upcoming open house'}
        </span>
        <span className={styles.eyebrowData}>
          <Time at={row.nextBooking.startsAt} date className={styles.eyebrowTime} />
          <span className={styles.eyebrowDot} aria-hidden="true" />
          <TimeRange
            start={row.nextBooking.startsAt}
            end={row.nextBooking.endsAt}
            className={styles.eyebrowTime}
          />
        </span>
      </span>
    );
  }
  return (
    <span className={styles.eyebrow}>
      <span className={styles.eyebrowMeta}>
        {row.bookingUntil
          ? `Bookable through ${formatCalendarDate(row.bookingUntil)}`
          : row.bookingHorizonDays != null
            ? `Bookable up to ${row.bookingHorizonDays} days out`
            : 'Open to book now'}
      </span>
    </span>
  );
}

export function ListingsEmpty({ canCreate }: { canCreate: boolean }) {
  return (
    <Card pad="lg" className={styles.empty}>
      <Stack gap={3} align="center">
        <span className={styles.emptyGlyph} aria-hidden="true">
          <HouseGlyph />
        </span>
        <Stack gap={1} align="center">
          <Heading level={4} as="h3">
            {canCreate ? 'No listings yet' : 'No listings to book yet'}
          </Heading>
          <Text size="sm" tone="soft" className={styles.emptyCopy}>
            {canCreate
              ? 'Add a listing to make it available for open houses. Agents can book any slot within the booking window you set.'
              : "There are no listings available for you to book right now. Check back soon: new open houses show up here as your group adds them."}
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}

export function ArchivedEmpty() {
  return (
    <Card pad="lg" className={styles.empty}>
      <Stack gap={3} align="center">
        <span className={styles.emptyGlyph} aria-hidden="true">
          <HouseGlyph />
        </span>
        <Stack gap={1} align="center">
          <Heading level={4} as="h3">
            No archived listings
          </Heading>
          <Text size="sm" tone="soft" className={styles.emptyCopy}>
            Listings you archive are hidden from booking and show up here. Archive a listing from its
            details page.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}

export function ListingsError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card pad="lg" className={styles.empty}>
      <Stack gap={3} align="center">
        <Heading level={4} as="h3">
          We couldn’t load your listings
        </Heading>
        <Text size="sm" tone="soft" className={styles.emptyCopy}>
          Something went wrong loading the listings. Try refreshing if it keeps happening.
        </Text>
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    </Card>
  );
}

export function ListingsSkeleton() {
  return (
    <ul className={styles.grid} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i}>
          <Card pad="md" spine className={styles.skeletonCard}>
            <Stack gap={2}>
              <span className={styles.skBlock} style={{ width: '60%', height: 'var(--space-3)' }} />
              <span className={styles.skBlock} style={{ width: '80%' }} />
              <span className={styles.skBlock} style={{ width: '45%', height: 'var(--space-3)' }} />
            </Stack>
          </Card>
        </li>
      ))}
    </ul>
  );
}

function PlusGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function HouseGlyph() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 10.5 12 4l8 6.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 9.5V20h13V9.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 20v-5h4v5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}
